import React, { useCallback, useMemo } from "react"
import { FareCardBase, FareCardBaseContent, FareCardFooter, InclusionContent } from "@silversea-ssc/ui-components-react"
import { Option } from "fp-ts/Option"
import { FarePriceResponse } from "@ssc-booking/api-contracts"
import { deepEqual } from "fast-equals"
import type { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups"
import clsx from "clsx"
import { FareInfographicBoxStatus } from "../../../../components/FareInfographicBox/FareInfographicBox"
import { AirClassOption, FareCard } from "../../_common/read-models/fare-card"
import { CruisePaymentTermsResponse } from "../../../_common/dot-com-api-types"
import { CardPromoBadgesContent, FareContent } from "../../../_common/sanity/sanity-types"
import { useECommerceMachine } from "../../../_common/machines/machines-context"
import { mapContentTokens } from "../../../_common/content-tokens"
import { buildPromoBadgesContent } from "../../_common/promo-badges"
import { useExpandableFareCard } from "../../../../components/ExpandableFareCardProvider"
import { computeVenetianSocietyStatus } from "./helpers"
import Infographic from "./Infographic/Infographic"
import { FarePriceFooter } from "./Infographic/FarePriceFooter"
import styles from "./FarePrice.module.scss"
import { FarePriceHeader } from "./Infographic/FarePriceHeader"
import InfographicExpandButton from "./Infographic/InfographicExpandButton"

type Props = {
    fareCard: FareCard
    paymentTerms: Option<CruisePaymentTermsResponse>
    inclusionsContent: InclusionContent[]
    status: FareInfographicBoxStatus
    cruiseGroup: CruiseGroupCode
    farePrice: FarePriceResponse
    promoBadgesContent: CardPromoBadgesContent[]
    fareContent: FareContent
    isRefreshingFarePrice: boolean
    faresAreLoading: boolean
}

const FarePriceLoaded: React.FC<React.PropsWithChildren<Props>> = ({
    cruiseGroup,
    status,
    paymentTerms,
    fareCard,
    inclusionsContent,
    farePrice,
    promoBadgesContent,
    fareContent,
    isRefreshingFarePrice,
    faresAreLoading,
}) => {
    const [state, send] = useECommerceMachine()

    const fareTitle = fareCard.fareLabel
    const currency = state.context.geoLocation.defaultCurrency
    const contentTokens = useMemo(
        () => mapContentTokens(fareCard.options, currency),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(fareCard.options), currency],
    )
    const venetianSocietyStatus = computeVenetianSocietyStatus(fareCard.vsEligibility, state.context.vsCode)
    const promoBadges = buildPromoBadgesContent(fareCard.options.promotions, promoBadgesContent)

    const onChoose = useCallback(() => {
        if (!farePrice) return
        send({ type: "SELECT_FARE", key: fareCard.key })
    }, [fareCard.key, farePrice, send])

    const onAirFareChanged = useCallback(
        (option: AirClassOption) => send({ type: "CHANGE_AIR_CLASS", airClass: option.airFare, key: fareCard.key }),
        [fareCard.key, send],
    )

    const onGatewayChanged = useCallback(
        (gatewayCode: string) => {
            send({ type: "CHANGE_GATEWAY", gatewayCode: gatewayCode, key: fareCard.key })
        },
        [fareCard.key, send],
    )

    const inclusionCodes = farePrice.price.inclusions.map((x) => x.inclusionCode)

    const d2dWithoutFlights = fareCard.fare == "DoorToDoor" && !FareCard.hasFlights(fareCard)
    const { isExpanded, onToggleFareCardExpandClick } = useExpandableFareCard()

    return (
        <FareCardBase
            fullHeight={false}
            trackingName={`fare_infographic_box_${fareCard.fare}${d2dWithoutFlights ? "_without_flights" : ""}`}
            className={clsx(styles.fareCards, "cy-fare-card")}
        >
            <FarePriceHeader
                fareContent={fareContent}
                fareTitle={fareContent.title}
                paymentTerms={paymentTerms}
                fareCode={fareCard.fare}
            />
            <FareCardFooter padding="small">
                <FarePriceFooter
                    status={status}
                    fareCard={fareCard}
                    onAirFareChanged={onAirFareChanged}
                    onChoose={onChoose}
                    onGatewayChanged={onGatewayChanged}
                    venetianSocietyStatus={venetianSocietyStatus}
                    promoBadges={promoBadges}
                    farePriceResponseState={
                        isRefreshingFarePrice ? { type: "loading" } : { type: "loaded", response: farePrice }
                    }
                    faresAreLoading={faresAreLoading}
                />
            </FareCardFooter>
            <Infographic
                className={styles.infographicContainer}
                fareTitle={fareTitle}
                enabledInclusions={inclusionCodes || []}
                inclusionsContent={inclusionsContent}
                cruiseGroup={cruiseGroup}
                inclusionsContentTokens={contentTokens}
            />
            <InfographicExpandButton
                isExpanded={isExpanded}
                onToggleFareCardExpandClick={onToggleFareCardExpandClick}
            />
        </FareCardBase>
    )
}

export default React.memo(FarePriceLoaded, deepEqual)
